import api from './axios';
import jsPDF from 'jspdf';

// ============================================================
// Investor Personality Archetype Report — Client-facing PDF
// Uses data from the archetype API (blurbs, definitions, themes)
// ============================================================

const purposeLabels = {
  emergency_fund: 'Emergency fund',
  short_term_savings: 'Short-term savings',
  specific_purchase: 'Saving for a specific purchase',
  retirement: 'Retirement planning',
  tax_free_savings: 'Tax-free savings',
  wealth_diversification: 'Wealth diversification',
  income_generation: 'Income generation',
  education: 'Education funding',
  estate_planning: 'Estate planning',
  offshore_diversification: 'Offshore diversification',
  other: 'Other'
};

const timeHorizonLabels = {
  less_than_1_year: 'Less than 1 year',
  '1_to_3_years': '1 to 3 years',
  '3_to_5_years': '3 to 5 years',
  '5_to_10_years': '5 to 10 years',
  more_than_10_years: 'More than 10 years',
  unsure: 'Unsure'
};

const liquidityLabels = {
  immediate_access: 'Yes, I may need access at short notice',
  partial_access: 'Yes, but only a portion',
  no_access_needed: 'No, I do not expect to need access soon',
  unsure: 'I am unsure'
};

const withdrawalLabels = {
  no_withdrawals: 'No withdrawals planned',
  once_off: 'Once-off withdrawal expected',
  regular_monthly: 'Regular monthly withdrawals',
  irregular: 'Irregular withdrawals may be needed',
  unsure: 'Unsure'
};

const experienceLabels = {
  no_knowledge: 'No knowledge about investing',
  some_research: 'Done some research',
  some_experience: 'Some experience with investing',
  experienced: 'Experienced investor',
  highly_experienced: 'Highly experienced investor'
};

const riskProfileLabels = {
  conservative: 'Conservative',
  moderate_conservative: 'Moderate Conservative',
  moderate: 'Moderate',
  moderate_aggressive: 'Moderate Aggressive',
  aggressive: 'Aggressive',
  unsure: 'I do not know'
};

const questionLabels = {
  purpose: 'Investment Purpose',
  time_horizon: 'Time Horizon',
  liquidity_need: 'Liquidity Needs',
  expected_withdrawals: 'Withdrawal Plans',
  experience: 'Investment Experience',
  stated_risk_profile: 'Self-Assessed Risk Profile'
};

const answerLabelMaps = {
  purpose: purposeLabels,
  time_horizon: timeHorizonLabels,
  liquidity_need: liquidityLabels,
  expected_withdrawals: withdrawalLabels,
  experience: experienceLabels,
  stated_risk_profile: riskProfileLabels
};

const metricConfig = {
  gain_attraction: { name: 'Gain Attraction', color: [43, 63, 101], lowLabel: 'Cautious', highLabel: 'Growth-Seeker' },
  loss_aversion: { name: 'Loss Aversion', color: [61, 82, 124], lowLabel: 'Resilient', highLabel: 'Protective' },
  volatility_anxiety: { name: 'Volatility Anxiety', color: [91, 85, 126], lowLabel: 'Calm', highLabel: 'Sensitive' },
  intervention_risk: { name: 'Intervention Risk', color: [108, 117, 143], lowLabel: 'Disciplined', highLabel: 'Hands-On' }
};

export async function generateArchetypeReport({ assessment, personalityProfile, themeId }) {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);
  let y = margin;
  let pageNum = 1;

  const client = assessment.client || {};
  const answers = assessment.investmentAnswers || [];
  const riskScore = assessment.riskScore || {};

  // Fetch archetype data from API
  let archetypeData = null;
  try {
    const res = await api.post('/archetypes/resolve', {
      gain_attraction_score: riskScore.gain_attraction_score || 0,
      loss_aversion_score: riskScore.loss_aversion_score || 0,
      volatility_anxiety_score: riskScore.volatility_anxiety_score || 0,
      intervention_risk_score: riskScore.intervention_risk_score || 0,
      theme_id: themeId
    });
    archetypeData = res.data.archetype;
  } catch (err) {
    console.error('Failed to resolve archetype:', err);
    archetypeData = {
      name: 'Investor Profile',
      tagline: 'Your personalised investment personality.',
      woven_narrative: 'Unable to load archetype data.',
      standalone_paragraphs: []
    };
  }

  // ---- Helpers ----
  const addFooter = () => {
    doc.setFontSize(8);
    doc.setTextColor(160, 160, 170);
    doc.setFont('helvetica', 'normal');
    doc.text('RDA Investor Personality Archetype Report \u2014 Confidential', margin, pageHeight - 10);
  };

  const checkPage = (needed) => {
    if (y + needed > pageHeight - 25) {
      addFooter();
      doc.addPage();
      pageNum++;
      y = margin;
    }
  };

  const drawGradientBar = (x, barY, width, height, score, rgb) => {
    doc.setFillColor(240, 240, 245);
    doc.roundedRect(x, barY, width, height, height / 2, height / 2, 'F');
    const fillWidth = Math.max(height, (score / 100) * width);
    doc.setFillColor(rgb[0], rgb[1], rgb[2]);
    doc.roundedRect(x, barY, fillWidth, height, height / 2, height / 2, 'F');
    const markerX = x + fillWidth - height / 2;
    const markerY = barY + height / 2;
    doc.setFillColor(255, 255, 255);
    doc.circle(markerX, markerY, height / 2 + 1, 'F');
    doc.setFillColor(rgb[0], rgb[1], rgb[2]);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    const scoreText = `${Math.round(score)}`;
    const tw = doc.getTextWidth(scoreText);
    doc.text(scoreText, markerX - tw / 2, markerY + 1.2);
  };

  const sectionHeader = (title, rgb) => {
    checkPage(20);
    doc.setFillColor(rgb[0], rgb[1], rgb[2]);
    doc.circle(margin + 3, y + 5, 3, 'F');
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 40);
    doc.text(title, margin + 10, y + 7.5);
    y += 16;
  };

  // Half-moon gauge: draws a semi-circular gauge with 5 quadrants and a needle
  const drawHalfMoonGauge = (cx, cy, radius, scorePercent) => {
    // 5 quadrant colours (mature navy palette, left to right)
    const quadrants = [
      { label: 'Conservative', color: [30, 58, 138] },        // 0–20%
      { label: 'Moderate\nConservative', color: [43, 63, 101] }, // 20–40%
      { label: 'Moderate', color: [61, 82, 124] },             // 40–60%
      { label: 'Moderate\nAggressive', color: [91, 85, 126] },  // 60–80%
      { label: 'Aggressive', color: [108, 117, 143] }           // 80–100%
    ];

    const totalAngle = Math.PI;
    const qAngle = totalAngle / 5;

    // Draw upward-curving half-moon
    // Angle 0 = left (Conservative), PI = right (Aggressive)
    // Negate sin for y to curve upward (PDF y-axis is inverted)
    quadrants.forEach((q, i) => {
      doc.setFillColor(q.color[0], q.color[1], q.color[2]);

      const steps = 20;
      for (let s = 0; s < steps; s++) {
        const a1 = Math.PI - (i * qAngle) - (s / steps) * qAngle;
        const a2 = Math.PI - (i * qAngle) - ((s + 1) / steps) * qAngle;
        const innerR = radius * 0.55;

        const x1 = cx + Math.cos(a1) * radius;
        const y1 = cy - Math.sin(a1) * radius;
        const x2 = cx + Math.cos(a2) * radius;
        const y2 = cy - Math.sin(a2) * radius;
        const ix1 = cx + Math.cos(a1) * innerR;
        const iy1 = cy - Math.sin(a1) * innerR;
        const ix2 = cx + Math.cos(a2) * innerR;
        const iy2 = cy - Math.sin(a2) * innerR;

        doc.triangle(x1, y1, x2, y2, ix1, iy1, 'F');
        doc.triangle(ix1, iy1, ix2, iy2, x2, y2, 'F');
      }
    });

    // Inner circle (white) to create the half-moon ring effect
    const innerR = radius * 0.55;
    doc.setFillColor(255, 255, 255);
    doc.circle(cx, cy, innerR, 'F');

    // Quadrant labels along the arc
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(60, 60, 70);
    quadrants.forEach((q, i) => {
      const midAngle = Math.PI - (i * qAngle) - (qAngle / 2);
      const labelR = radius + 10;
      const lx = cx + Math.cos(midAngle) * labelR;
      const ly = cy - Math.sin(midAngle) * labelR;
      const lines = q.label.split('\n');
      lines.forEach((line, li) => {
        doc.text(line, lx, ly + li * 4, { align: 'center' });
      });
    });

    // Tick marks at quadrant boundaries
    doc.setDrawColor(180, 180, 190);
    doc.setLineWidth(0.3);
    for (let i = 0; i <= 5; i++) {
      const angle = Math.PI - (i * qAngle);
      const x1 = cx + Math.cos(angle) * (radius - 1);
      const y1 = cy - Math.sin(angle) * (radius - 1);
      const x2 = cx + Math.cos(angle) * (radius + 2);
      const y2 = cy - Math.sin(angle) * (radius + 2);
      doc.line(x1, y1, x2, y2);
    }

    // Needle — points upward into the arc
    const clampedScore = Math.max(0, Math.min(100, scorePercent));
    const needleAngle = Math.PI - (clampedScore / 100) * totalAngle;
    const needleLength = radius * 0.75;
    const needleBaseWidth = 2.5;

    const nx = cx + Math.cos(needleAngle) * needleLength;
    const ny = cy - Math.sin(needleAngle) * needleLength;

    const perpAngle = needleAngle + Math.PI / 2;
    const bx1 = cx + Math.cos(perpAngle) * needleBaseWidth;
    const by1 = cy - Math.sin(perpAngle) * needleBaseWidth;
    const bx2 = cx - Math.cos(perpAngle) * needleBaseWidth;
    const by2 = cy + Math.sin(perpAngle) * needleBaseWidth;

    doc.setFillColor(50, 50, 60);
    doc.triangle(nx, ny, bx1, by1, bx2, by2, 'F');

    // Center dot
    doc.setFillColor(50, 50, 60);
    doc.circle(cx, cy, 3, 'F');
    doc.setFillColor(255, 255, 255);
    doc.circle(cx, cy, 1.5, 'F');

    // Score percentage text above gauge
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(50, 50, 60);
    doc.text(`${Math.round(clampedScore)}%`, cx, cy - radius * 0.3, { align: 'center' });
  };

  // ===== COVER / HEADER =====
  // Gradient-style header
  doc.setFillColor(30, 58, 138);
  doc.rect(0, 0, pageWidth, 55, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(26);
  doc.setFont('helvetica', 'bold');
  doc.text('Investor Personality Archetype', margin, 22);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`${archetypeData.name}`, margin, 34);
  doc.setFontSize(10);
  doc.text(`Prepared for: ${client.full_name || 'Client'}`, margin, 46);
  doc.text(`Date: ${new Date().toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' })}`, pageWidth - margin, 46, { align: 'right' });

  y = 68;

  // ===== 1. Investor personality archetype =====
  checkPage(35);
  sectionHeader('Investor personality archetype', [108, 117, 143]);

  // Intro line
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(60, 60, 70);
  doc.text('The results of your personalised investor assessment identify you as:', margin + 4, y);
  y += 9;

  // Archetype name — one size larger, bold
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(60, 60, 70);
  doc.text(archetypeData.name, margin + 4, y);
  y += 8;

  // Tagline — same size as intro, italic
  doc.setFontSize(10);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(100, 100, 110);
  doc.text(`"${archetypeData.tagline}"`, margin + 4, y);
  y += 12;

  // ===== 2. Investment perspective (Woven Narrative) =====
  checkPage(30);
  sectionHeader('Investment perspective', [91, 85, 126]);

  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(50, 50, 60);
  const wovenLines = doc.splitTextToSize(archetypeData.woven_narrative, contentWidth - 8);
  checkPage(wovenLines.length * 4.5 + 6);
  doc.text(wovenLines, margin + 4, y);
  y += wovenLines.length * 4.5 + 10;

  // ===== Investment approach (Half-moon gauge) =====
  y += 5;
  sectionHeader('Investment approach', [91, 85, 126]);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(60, 60, 70);
  doc.text('Your answers suggest the following investment approach:', margin + 4, y);
  y += 12;

  const finalRiskScore = parseFloat(riskScore.overall_risk_score) || 0;
  const gaugeCx = pageWidth / 2;
  const gaugeCy = y + 48;
  const gaugeRadius = 42;
  drawHalfMoonGauge(gaugeCx, gaugeCy, gaugeRadius, finalRiskScore);
  y = gaugeCy + 20;

  // ===== 3. Investment personality attributes =====
  y += 5;
  sectionHeader('Investment personality attributes', [61, 82, 124]);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 90);
  doc.text('Each dimension measures a different aspect of how you think and feel about investing.', margin, y);
  y += 10;

  const metrics = [
    { key: 'gain_attraction_score', metricKey: 'gain_attraction' },
    { key: 'loss_aversion_score', metricKey: 'loss_aversion' },
    { key: 'volatility_anxiety_score', metricKey: 'volatility_anxiety' },
    { key: 'intervention_risk_score', metricKey: 'intervention_risk' }
  ];

  metrics.forEach(metric => {
    const cfg = metricConfig[metric.metricKey];
    const score = riskScore[metric.key] || 0;
    const rgb = cfg.color;

    checkPage(14);

    // Label on left, percentage on right
    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 40);
    doc.text(cfg.name, margin + 4, y + 5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 90);
    doc.text(`${Math.round(score)}%`, margin + contentWidth - 4, y + 5, { align: 'right' });

    // Bar underneath
    const barY = y + 7.5;
    const barHeight = 4;
    const barWidth = contentWidth - 8;

    doc.setFillColor(240, 240, 245);
    doc.roundedRect(margin + 4, barY, barWidth, barHeight, barHeight / 2, barHeight / 2, 'F');

    const fillWidth = Math.max(barHeight, (score / 100) * barWidth);
    doc.setFillColor(rgb[0], rgb[1], rgb[2]);
    doc.roundedRect(margin + 4, barY, fillWidth, barHeight, barHeight / 2, barHeight / 2, 'F');

    // Low/high labels
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(150, 150, 160);
    doc.text(cfg.lowLabel, margin + 4, barY + barHeight + 4);
    doc.text(cfg.highLabel, margin + 4 + barWidth, barY + barHeight + 4, { align: 'right' });

    y += 18;
  });

  // ===== 4. Attribute overview (Standalone) =====
  checkPage(30);
  y += 3;
  sectionHeader('Attribute overview', [43, 63, 101]);

  if (archetypeData.standalone_paragraphs && archetypeData.standalone_paragraphs.length > 0) {
    archetypeData.standalone_paragraphs.forEach((para, idx) => {
      checkPage(30);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 30, 40);
      doc.text(`${para.metric}: ${para.title}`, margin + 4, y);
      y += 6;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 70);
      const lines = doc.splitTextToSize(para.body, contentWidth - 8);
      checkPage(lines.length * 4.5 + 6);
      doc.text(lines, margin + 4, y);
      y += lines.length * 4.5 + 6;
    });
  }

  // ===== 5. Investment goals =====
  doc.addPage();
  pageNum++;
  y = margin;
  sectionHeader('Investment goals', [30, 58, 138]);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 90);
  doc.text('Here\'s a summary of what you told us about your investment goals:', margin, y);
  y += 10;

  const goalQuestions = ['purpose', 'time_horizon', 'liquidity_need', 'expected_withdrawals', 'experience', 'stated_risk_profile'];
  goalQuestions.forEach(key => {
    const answer = answers.find(a => a.question_key === key);
    if (!answer) return;
    const qLabel = questionLabels[key] || key;
    const aLabel = (answerLabelMaps[key] && answerLabelMaps[key][answer.answer_value]) || answer.answer_value || '\u2014';

    checkPage(20);
    doc.setFillColor(248, 249, 252);
    doc.roundedRect(margin, y, contentWidth, 16, 3, 3, 'F');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100, 100, 110);
    doc.text(qLabel, margin + 10, y + 6);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(30, 30, 40);
    doc.text(aLabel, margin + 10, y + 12.5);
    y += 20;
  });

  // ===== 6. Next step =====
  checkPage(40);
  y += 8;
  sectionHeader('Next step', [25, 45, 110]);

  doc.setFillColor(240, 242, 255);
  doc.roundedRect(margin, y, contentWidth, 35, 6, 6, 'F');

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(60, 60, 70);
  const advisorMsg = 'Based on your goals and investor personality, a financial advisor can recommend a specific portfolio or asset allocation suited to you. This report gives you \u2014 and your advisor \u2014 a shared language to have that conversation.';
  const advisorLines = doc.splitTextToSize(advisorMsg, contentWidth - 24);
  doc.text(advisorLines, margin + 12, y + 10);

  doc.setFillColor(30, 58, 138);
  doc.roundedRect(margin + 12, y + 22, 60, 9, 5, 5, 'F');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('Book a Consultation', margin + 16, y + 28.5);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 120);
  doc.text('RDA Financial Services  |  marco@rdafs.co.za', margin + 80, y + 28.5);

  y += 42;

  // ===== DISCLOSURE =====
  checkPage(30);
  y += 3;
  doc.setFillColor(250, 250, 252);
  doc.roundedRect(margin, y, contentWidth, 22, 4, 4, 'F');
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(80, 80, 90);
  doc.text('DISCLOSURE', margin + 8, y + 6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 90);
  const disclosureLines = doc.splitTextToSize(
    'This report is for informational purposes only and does not constitute financial advice. The investor personality archetype is derived from a questionnaire and is intended to facilitate a conversation with a qualified financial advisor. It should not be used as the sole basis for any investment decision. Portfolio and asset allocation recommendations are excluded from this report and reserved for a personal consultation with your advisor.',
    contentWidth - 16
  );
  doc.text(disclosureLines, margin + 8, y + 11);

  // Stamp footers on all pages
  const totalPages = pageNum;
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(160, 160, 170);
    doc.setFont('helvetica', 'normal');
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
    doc.text('RDA Investor Personality Archetype Report — Confidential', margin, pageHeight - 10);
  }

  const clientName = (client.full_name || 'Client').replace(/[^a-zA-Z0-9 ]/g, '').trim();
  doc.save(`${clientName} - Investor Personality Archetype.pdf`);
}
